import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import AppContext from '../../../../Context/AppContext';
import configData from '../../../../config.json';
import OolaWheelMilestoneV2 from '../OolaWheelMilestoneV2';
import ModalErrorV2 from '../../Modals/ModalErrorV2';
import { readableFColor } from '../../../../util/readableBrandColor';
import './SelectOolaOneV2.css';

function formatDate(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function goalKey(goal, index) {
    if (goal?.GoalId != null) return `g-${goal.GoalId}`;
    if (goal?.idL) return `g-${goal.idL}`;
    if (goal?.id) return `g-${goal.id}`;
    return `g-${goal?.areaId ?? 'x'}-${goal?.recomendationIndex ?? index}`;
}

function SummitIcon() {
    return (
        <svg
            className="pv2-oola1__summit-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
        >
            <path
                d="M4 20h16M7 20l5-9 5 9M9.5 11l1.5-2.5L14 11"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15.5 8.5l1.2-1.8 2.3 3.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function OolaOneFlagIcon() {
    return (
        <svg
            className="pv2-oola1__flag-icon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
    );
}

/**
 * Portal V2 — Select your Oola One (priority goal for the next 365 days).
 * Classic SelectOneSectionConfirm stays on /steps.
 */
export default function SelectOolaOneV2({
    AllState,
    changeData,
    forceUpdate: _forceUpdate,
    handleChangeHeader,
    onContinue,
}) {
    const { checkUserSession, changeLoading } = useContext(AppContext);
    const currUser = checkUserSession();
    const seven = (AllState.SelectedGoalsSeven || []).filter(Boolean).slice(0, 7);

    const selectedIndex = seven.findIndex((g) => g?.selectedOne === true);
    const selected = selectedIndex >= 0 ? seven[selectedIndex] : null;

    const [errorOpen, setErrorOpen] = useState(false);
    const [errorTitle, setErrorTitle] = useState('Select your Oola One');
    const [errorDesc, setErrorDesc] = useState(
        'Choose the single goal you most want to achieve in the next 365 days.'
    );

    useEffect(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }, []);

    const selectGoal = (index) => {
        if (index < 0 || index >= seven.length) return;
        const nextSeven = seven.map((g, i) => ({
            ...g,
            selectedOne: i === index,
        }));
        const pick = nextSeven[index];
        if (currUser?.userStepId != 7 && handleChangeHeader) {
            handleChangeHeader(1);
        }
        changeData({
            ...AllState,
            SelectedGoalsSeven: nextSeven,
            SelectedOolaOne: pick,
        });
    };

    const finish = () => {
        const pick = seven.find((x) => x.selectedOne === true);

        if (!pick) {
            setErrorTitle('Select your Oola One');
            setErrorDesc(
                'Choose the single goal you most want to achieve in the next 365 days.'
            );
            setErrorOpen(true);
            return;
        }

        if (pick.GoalId == null) {
            // Preview / demo without persisted GoalId — advance UI only
            if (onContinue) onContinue();
            return;
        }

        changeLoading(true);
        const url = `${configData.SERVER_URL}Goals/SetOolaOne/${pick.GoalId}`;
        axios
            .put(
                url,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${currUser.token}`,
                    },
                }
            )
            .then(() => {
                changeLoading(false);
                if (onContinue) onContinue();
            })
            .catch((error) => {
                console.log(error);
                changeLoading(false);
                setErrorTitle('Could not save Oola One');
                setErrorDesc(
                    'Something went wrong saving your priority goal. Please try again.'
                );
                setErrorOpen(true);
            });
    };

    const heroF = selected ? AllState.Fs?.[selected.areaId] || {} : null;
    const heroAccent = heroF
        ? readableFColor(heroF.name, heroF.color || '#15c285')
        : '#efd078';
    const wheelActive =
        selected?.areaId != null ? Number(selected.areaId) : -1;

    return (
        <section className="pv2-oola1">
            <aside className="pv2-oola1__side">
                <div className="pv2-oola1__wheel">
                    <OolaWheelMilestoneV2
                        Fs={AllState.Fs || []}
                        activeIndex={wheelActive}
                        focusScore={
                            heroF?.score != null
                                ? Number(heroF.score).toFixed(1)
                                : null
                        }
                        hideFooter
                    />
                </div>
                <p className="pv2-oola1__wheel-hint">
                    Choose the one goal that matters most this year.
                </p>
            </aside>

            <div className="pv2-oola1__panel">
                <header className="pv2-oola1__head">
                    <div className="pv2-oola1__titles">
                        <h2 className="pv2-oola1__title">
                            <span className="pv2-oola1__copy-desk">
                                Choose your Oola One Goal
                            </span>
                            <span className="pv2-oola1__copy-mob">
                                Choose your Oola One Goal
                            </span>
                        </h2>
                        <p className="pv2-oola1__lead">
                            <span className="pv2-oola1__copy-desk">
                                Select the single goal you most want to achieve
                                in the next 365 days.
                            </span>
                            <span className="pv2-oola1__copy-mob">
                                Pick the one goal you most want to achieve in
                                the next 365 days.
                            </span>
                        </p>
                    </div>
                </header>

                {selected ? (
                    <div
                        className="pv2-oola1__hero pv2-oola1__hero--summit"
                        style={{ ['--pv2-o1-accent']: heroAccent }}
                    >
                        <div className="pv2-oola1__hero-top">
                            <span className="pv2-oola1__hero-k">
                                <SummitIcon />
                                <OolaOneFlagIcon />
                                Your Oola One
                            </span>
                            <span className="pv2-oola1__hero-f">
                                {heroF?.name || 'Goal'}
                            </span>
                            {selected.date ? (
                                <span className="pv2-oola1__hero-date">
                                    {formatDate(selected.date)}
                                </span>
                            ) : null}
                        </div>
                        <strong className="pv2-oola1__hero-text">
                            {selected.text}
                        </strong>
                    </div>
                ) : (
                    <div className="pv2-oola1__hero pv2-oola1__hero--empty">
                        <span className="pv2-oola1__hero-k">
                            <SummitIcon />
                            <OolaOneFlagIcon />
                            Your Oola One
                        </span>
                        <p className="pv2-oola1__hero-empty">
                            <span className="pv2-oola1__copy-desk">
                                Select a goal below to set your Oola One.
                            </span>
                            <span className="pv2-oola1__copy-mob">
                                Tap a goal below to set it as your priority
                                focus
                            </span>
                        </p>
                    </div>
                )}

                <div className="pv2-oola1__panel-scroll">
                    {seven.length === 0 ? (
                        <p className="pv2-oola1__empty">
                            No Top 7 goals yet. Go back and arrange your seven
                            goals first.
                        </p>
                    ) : null}

                    <ul className="pv2-oola1__list">
                    {seven.map((item, index) => {
                        const f = AllState.Fs?.[item.areaId] || {};
                        const accent = readableFColor(
                            f.name,
                            f.color || '#15c285'
                        );
                        const isSelected = item.selectedOne === true;
                        return (
                            <li key={goalKey(item, index)}>
                                <button
                                    type="button"
                                    className={`pv2-oola1__card${
                                        isSelected ? ' is-selected' : ''
                                    }`}
                                    style={{ ['--pv2-o1-accent']: accent }}
                                    onClick={() => selectGoal(index)}
                                    aria-pressed={isSelected}
                                >
                                    <span
                                        className="pv2-oola1__rank"
                                        aria-hidden="true"
                                    >
                                        {index + 1}
                                    </span>
                                    <div className="pv2-oola1__body">
                                        <div className="pv2-oola1__meta">
                                            <span className="pv2-oola1__f">
                                                {f.name || 'Goal'}
                                            </span>
                                            {item.date ? (
                                                <span className="pv2-oola1__date">
                                                    {formatDate(item.date)}
                                                </span>
                                            ) : null}
                                        </div>
                                        <span className="pv2-oola1__text">
                                            {item.text}
                                        </span>
                                    </div>
                                    <span
                                        className={`pv2-oola1__check${
                                            isSelected ? ' is-on' : ''
                                        }`}
                                        aria-hidden="true"
                                    >
                                        {isSelected ? (
                                            <svg
                                                viewBox="0 0 16 16"
                                                width="14"
                                                height="14"
                                            >
                                                <path
                                                    d="M3 8.5 L6.5 12 L13 4.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        ) : null}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                    </ul>
                </div>

                <div className="pv2-oola1__actions">
                    <button
                        type="button"
                        className={`pv2-oola1__cta${
                            selected ? ' is-ready' : ' is-disabled'
                        }`}
                        disabled={!selected}
                        onClick={finish}
                    >
                        Continue
                    </button>
                </div>
            </div>

            <ModalErrorV2
                show={errorOpen}
                setShowError={setErrorOpen}
                ModalErrorTitle={errorTitle}
                ModalErrorDesc={errorDesc}
            />
        </section>
    );
}
